import React, { ChangeEvent, useMemo, useState } from 'react';
import { DatePickerField } from '@ovotech/element-v5';
import { fromDate, toDateString } from '../../utils/movingDateValuesConversion';
import { addDays, format } from 'date-fns';
import { invalidMovingDateError } from '../../utils/validationUtils';

import { DateFormat } from '@/src/types/MoveOut';
import {
  CTAButton,
  ErrorNotification,
  Modal,
  P,
  SecondaryCTAButton,
} from '@ovotech/element-v4';
import { LoadingContent } from '@ovotech/energy-cx/dist/shared/core/components';
import styles from './ChangeMovingDateModal.module.css';
import { useAccountContext, AnalyticsClick } from '@ovotech/ui-tools';
import {
  HOME_MOVES_CHANGE_DATE_MODAL_CONFIRM,
  HOME_MOVES_CHANGE_DATE_MODAL_CANCEL,
} from '@/src/constants/analytics-subjects';
import { useUpdateMoveOutDateMutation } from '@/src/api/home-moves';

type ChangeMovingDateModalProps = {
  onMoveOutUpdated: (movingDate: DateFormat) => void;
  onClose: () => void;
  initialMovingDate: Date;
};

/**
 * Modal component for changing the move-out date using the new Home Moves API
 */
export const ChangeMovingDateModal = ({
  onMoveOutUpdated,
  onClose,
  initialMovingDate,
}: ChangeMovingDateModalProps) => {
  const [movingDate, setMovingDate] = useState<DateFormat | undefined>(
    fromDate(initialMovingDate)?.date,
  );

  const { min, max } = useMemo(() => {
    const now = new Date();
    return {
      min: format(addDays(now, -28), 'YYYY-MM-DD'),
      max: format(addDays(now, 27), 'YYYY-MM-DD'),
    };
  }, []);

  const { accountId } = useAccountContext();
  const updateMoveOutDateMutation = useUpdateMoveOutDateMutation();
  const [error, setError] = useState<string | undefined>();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      setMovingDate(undefined);
      return;
    }
    const newMovingDate = fromDate(new Date(value));
    if (!newMovingDate) {
      return;
    }
    setMovingDate(newMovingDate.date);
  };

  const handleConfirm = async () => {
    if (!accountId) return;

    if (!movingDate) {
      setError(invalidMovingDateError);
      return;
    }

    try {
      // Call the new API endpoint to update the move-out date
      await updateMoveOutDateMutation.mutateAsync({
        accountId,
        data: {
          moveOutDate: toDateString({ date: movingDate }),
        },
      });

      // Success! Notify parent component
      onMoveOutUpdated(movingDate);
    } catch (error) {
      // Error handling is managed by the mutation's onError callback
      console.error('Failed to update move-out date:', error);
    }
  };

  return (
    <Modal title="Change your moving day" isOpen={true} onClose={onClose}>
      {updateMoveOutDateMutation.isError ? (
        <ErrorNotification
          title="Sorry, there's a bit of a problem"
          id={'move-out-api-error'}
        >
          <P>
            We&apos;re working hard to fix it. But in the meantime, please try
            refreshing the page.
          </P>
        </ErrorNotification>
      ) : null}
      <DatePickerField
        id="change-moving-date-field"
        label="When is your new moving day?"
        value={movingDate ? toDateString({ date: movingDate }) : ''}
        error={error}
        min={min}
        max={max}
        onChange={handleChange}
      />

      <div className={styles.buttonsGroup}>
        <AnalyticsClick
          name={HOME_MOVES_CHANGE_DATE_MODAL_CONFIRM}
          properties={{
            newMovingDate: movingDate
              ? `${movingDate.day}/${movingDate.month}/${movingDate.year}`
              : '',
          }}
        >
          <CTAButton onClick={handleConfirm}>
            <LoadingContent loading={updateMoveOutDateMutation.isLoading}>
              Confirm date
            </LoadingContent>
          </CTAButton>
        </AnalyticsClick>

        <AnalyticsClick
          name={HOME_MOVES_CHANGE_DATE_MODAL_CANCEL}
          properties={{
            currentMovingDate: movingDate
              ? `${movingDate.day}/${movingDate.month}/${movingDate.year}`
              : '',
          }}
        >
          <SecondaryCTAButton onClick={onClose}>Cancel</SecondaryCTAButton>
        </AnalyticsClick>
      </div>
    </Modal>
  );
};
